import { Plus } from 'lucide-react';
import { useState } from 'react';

import { DataTable, DataTableCell, DataTableHead, DataTableRow } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { type AiSpendLimit, aiApi, num, SPEND_PRODUCTS } from '@/lib/aiAdmin';
import { formatDateTime, formatUsd } from '@/lib/format';

import { CENTER, columns, confirmedWrite, errorText, Loading, LoadFailed, type Organizations, SectionLabel, TITLE, useLoad } from './common';

// A Select item cannot have an empty value, so the empty organization id (the default for all, as in billing) has its own item value.
const NO_ORG_ITEM = '-';
const AMOUNT = /^\d{1,12}(\.\d{1,6})?$/;
/** Billing's canonical decimal has trailing zeros ("250.000000"); the form shows "250". */
const plain = (amount: string) => (amount.includes('.') ? amount.replace(/0+$/, '').replace(/\.$/, '') : amount);

/** `organization` is '' for the default for every organization. */
type Draft = { organization: string; product: string; amount: string; isNew: boolean };

/** Adds a limit, or changes the amount of one. The organization and the product of a stored limit are its key, so they stay fixed. */
function LimitDialog({ draft, onClose, onSaved, orgs }: { draft: Draft; onClose: () => void; onSaved: () => void; orgs: Organizations }) {
  const [state, setState] = useState(draft);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const products = [...new Set([...SPEND_PRODUCTS, state.product])];

  const save = async () => {
    if (!AMOUNT.test(state.amount.trim())) return setError('Enter the limit in USD, such as 250 or 12.50.');
    setSaving(true);
    setError(undefined);
    try {
      await aiApi.putSpendLimit({ ...(state.organization ? { organizationId: state.organization } : {}), product: state.product, monthlyLimitUsd: state.amount.trim() });
      onSaved();
    } catch (err) {
      setError(errorText(err, 'The save failed'));
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={draft.isNew ? 'Add a spend limit' : 'Change a spend limit'}
      description="A limit is per UTC calendar month. A limit of an organization overrides the default for the same product."
      footer={
        <>
          <Button type="button" variant="outline" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={saving} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <div className="space-y-1.5">
          <Label>Organization</Label>
          {orgs.error && draft.isNew ? (
            <div role="alert" className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-destructive">
              <span>The organizations could not be loaded: {orgs.error}</span>
              <Button type="button" size="sm" variant="outline" onClick={orgs.reload}>
                Retry
              </Button>
            </div>
          ) : (
            <Select value={state.organization || NO_ORG_ITEM} onValueChange={(item) => setState({ ...state, organization: item === NO_ORG_ITEM ? '' : item })} disabled={!draft.isNew}>
              <SelectTrigger aria-label="Organization">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_ORG_ITEM}>Default (all organizations)</SelectItem>
                {[...new Set([...orgs.rows.map((row) => row.organizationId), state.organization])]
                  .filter(Boolean)
                  .map((id) => (
                    <SelectItem key={id} value={id}>
                      {orgs.nameOf(id)}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="space-y-1.5">
          <Label>Product</Label>
          <Select value={state.product} onValueChange={(product) => setState({ ...state, product })} disabled={!draft.isNew}>
            <SelectTrigger aria-label="Product">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {products.map((id) => (
                <SelectItem key={id} value={id}>
                  {id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="spend-limit-amount">Monthly limit (USD)</Label>
          <Input id="spend-limit-amount" inputMode="decimal" value={state.amount} onChange={(event) => setState({ ...state, amount: event.target.value })} />
        </div>
        {error ? (
          <p role="alert" className="text-destructive">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

type Loaded = { filter: string; limits: AiSpendLimit[]; unavailable?: false } | { unavailable: true };

/** The monthly spend limits of billing, per organization and product, with this month's spend. Add, change and remove. */
export function LimitsTab({ orgs }: { orgs: Organizations }) {
  // '' shows every limit; an organization id shows that organization's limits and the defaults.
  const [filter, setFilter] = useState('');
  const [draft, setDraft] = useState<Draft | undefined>();
  const load = useLoad<Loaded>(async () => {
    try {
      return { filter, limits: (await aiApi.spendLimits(filter || undefined)).limits ?? [] };
    } catch (error) {
      // Until billing has the spend limit calls, the API answers 501.
      if ((error as { status?: number }).status === 501) return { unavailable: true };
      throw error;
    }
  }, [filter]);
  // The load keeps the old answer while the new one comes, so rows of another filter are never shown.
  const fresh = load.data && !load.data.unavailable && load.data.filter === filter;
  const limits = load.data && !load.data.unavailable && fresh ? load.data.limits : [];

  const remove = async (limit: AiSpendLimit) => {
    const where = limit.organizationId ? limit.organizationName || orgs.nameOf(limit.organizationId) : 'the default';
    const ok = await confirmedWrite(
      `Remove the ${limit.product} limit of ${where}?`,
      () => aiApi.putSpendLimit({ ...(limit.organizationId ? { organizationId: limit.organizationId } : {}), product: limit.product, clear: true }),
      'Limit removed',
      'The removal failed',
    );
    if (ok) load.reload();
  };

  if (load.error) return <LoadFailed message={load.error} onRetry={load.reload} />;
  if (!load.data) return <Loading label="Loading spend limits" />;
  if (load.data.unavailable) {
    return (
      <div className="rounded-xl border border-border bg-card px-4 py-8 text-center text-sm text-muted-foreground">
        <p className="font-medium text-foreground">Spend limits are not available yet.</p>
        <p className="mt-1">Billing does not have the spend limit calls yet. This tab works when it does.</p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <SectionLabel
        actions={
          <Button type="button" size="sm" onClick={() => setDraft({ organization: filter, product: SPEND_PRODUCTS[0]!, amount: '', isNew: true })}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add limit
          </Button>
        }
      >
        Monthly spend limits
      </SectionLabel>
      {orgs.error ? <LoadFailed message={`The organizations could not be loaded: ${orgs.error}`} onRetry={orgs.reload} /> : null}
      <DataTable
        toolbar={
          orgs.error ? undefined : (
            <Select value={filter || NO_ORG_ITEM} onValueChange={(item) => setFilter(item === NO_ORG_ITEM ? '' : item)}>
              <SelectTrigger size="sm" className="w-64" aria-label="Organization filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_ORG_ITEM}>All organizations</SelectItem>
                {orgs.rows.map((row) => (
                  <SelectItem key={row.organizationId} value={row.organizationId}>
                    {orgs.nameOf(row.organizationId)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        }
      >
        <DataTableHead columns={columns(['Organization', 'Product', 'Limit', 'Spent this month', 'Updated by', 'Updated', ''])} />
        <tbody>
          {limits.map((limit) => (
            <DataTableRow key={`${limit.organizationId ?? ''}/${limit.product}`}>
              <DataTableCell className={TITLE}>{limit.organizationId ? <span title={limit.organizationId}>{limit.organizationName || orgs.nameOf(limit.organizationId)}</span> : 'Default (all organizations)'}</DataTableCell>
              <DataTableCell className={CENTER}>{limit.product}</DataTableCell>
              <DataTableCell className={`${CENTER} tabular-nums`}>{formatUsd(num(limit.monthlyLimitUsd))}</DataTableCell>
              <DataTableCell className={`${CENTER} tabular-nums`}>{limit.spentThisMonthUsd ? formatUsd(num(limit.spentThisMonthUsd)) : '—'}</DataTableCell>
              <DataTableCell className={CENTER}>{limit.updatedBy || '—'}</DataTableCell>
              <DataTableCell className={CENTER}>{formatDateTime(limit.updatedAt)}</DataTableCell>
              <DataTableCell className={CENTER}>
                <div className="inline-flex gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => setDraft({ organization: limit.organizationId ?? '', product: limit.product, amount: plain(limit.monthlyLimitUsd), isNew: false })}>
                    Edit
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => void remove(limit)}>
                    Remove
                  </Button>
                </div>
              </DataTableCell>
            </DataTableRow>
          ))}
          {!fresh ? (
            <tr>
              <td colSpan={7}>
                <Loading label="Loading spend limits" />
              </td>
            </tr>
          ) : limits.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                No spend limits.
              </td>
            </tr>
          ) : null}
        </tbody>
      </DataTable>
      {draft ? (
        <LimitDialog
          draft={draft}
          orgs={orgs}
          onClose={() => setDraft(undefined)}
          onSaved={() => {
            setDraft(undefined);
            load.reload();
          }}
        />
      ) : null}
    </div>
  );
}
